import { CatalogOverviewResponse } from "@booyaltd/core";
import { CampaignRecord } from "../api/entities/CampaignRecord";

// The one answer to "is there a live campaign at /c/<slug>?" (#596), shared
// by the campaign landing page and the link.farmflix.tv resolver (#883) so
// the two can never disagree about which slugs are live.
//
// The overview payload carries `campaigns` ahead of the @booyaltd/core type
// bump — read it via a local extension type (precedent: OverviewContext's
// own @ts-ignore casts around the external overview).
export type OverviewWithCampaigns = CatalogOverviewResponse & {
  campaigns?: CampaignRecord[];
};

// A record the template can actually render: a live window alone is not
// enough — body copy and a CTA href are load-bearing, and a malformed
// record must fail closed to the Home redirect, never crash the page.
const isRenderableRecord = (record: CampaignRecord): boolean =>
  record.active &&
  typeof record.bodyCopy === "string" &&
  record.bodyCopy.trim().length > 0 &&
  !!record.cta &&
  typeof record.cta.href === "string" &&
  record.cta.href.length > 0;

// The server only serves active, in-window campaigns, but the public
// overview is cached — re-check the window at call time (and reject a
// record too malformed to render). Undefined when the slug is unknown,
// switched off, out of window, or the overview has no campaigns key.
export const findLiveCampaign = (
  overview: CatalogOverviewResponse | undefined,
  slug: string | undefined,
  now: number = Date.now(),
): CampaignRecord | undefined => {
  const campaigns = (overview as OverviewWithCampaigns | undefined)?.campaigns;
  const record = campaigns?.find((campaign) => campaign.slug === slug);

  if (!record || !isRenderableRecord(record)) {
    return undefined;
  }

  // Positive checks so an unparseable date (NaN) fails closed: every
  // comparison with NaN is false.
  const startsAt = Date.parse(record.startsAt);
  const endsAt = Date.parse(record.endsAt);
  const inWindow = startsAt <= now && now <= endsAt;

  return inWindow ? record : undefined;
};
